import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EditIcon,
  LeftArrowIcon,
  LockIcon,
  TipsIcon
} from '@/components/common/icon';
import { Alert, Drawer, Tooltip } from 'aha-components';
import { isMobile } from '@/biz/tool';
import EstimateFairRange, {
  getFairRangeDefaultValue,
  isEmptyFairRangeValue
} from './EstimatePriceComponents/EstimateFairRange';
import EstimateFairTips from './EstimatePriceComponents/EstimateFairTips';
import axiosInstance from '@/apis/axiosInstance';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { formatPriceDollar } from '@/biz/order';

function formatPriceWithCommas(value: number | string | undefined | null) {
  if (value === '' || value === undefined || value === null) return '0';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default function ShareRateInput({
  setNewOrderPrice,
  newOrderPrice,
  isCounterOffer = false,
  lastAdsOfferAmount,
  lastCreatorOfferAmount,
  disabled = false,
  visible
}: {
  setNewOrderPrice: (price: number | string) => void;
  newOrderPrice: number | string;
  isCounterOffer?: boolean;
  lastAdsOfferAmount?: number;
  lastCreatorOfferAmount?: number;
  disabled?: boolean;
  visible?: boolean;
}) {
  const { t } = useTranslation();
  const {
    selectedPriceFields,
    creatorLowActualGet,
    creatorHighActualGet,
    creatorMaxActualGet
  } = useMultiplePricesStore();
  const { detailData } = useDetailDataStore();
  const { order } = detailData;
  const { negotiationHistory } = order;
  const submitedBy =
    negotiationHistory?.find((item: any) => !!item.nonNegotiable)?.submitedBy ??
    null;
  const low = creatorLowActualGet;
  const high = creatorHighActualGet;
  const [isEditing, setIsEditing] = useState(false);
  const hasManuallyAdjustedRef = useRef(false);
  const hasSetCounterOfferInitialRef = useRef(false);
  const prevLowHighRef = useRef<{
    low: number | null;
    high: number | null;
  } | null>(null);
  const setNewOrderPriceFromUser = useCallback(
    (price: number | string) => {
      hasManuallyAdjustedRef.current = true;
      setNewOrderPrice(price);
    },
    [setNewOrderPrice]
  );
  const displayPrice = String(newOrderPrice ?? '') || '0';
  const [setBaseRateSuccess, setSetBaseRateSuccess] = useState(false);
  // 用来判断议价时填写的值是否在广告主和达人出过价的范围内，不在不允许提交
  const [isPriceRange, setIsPriceRange] = useState(true);
  const [showFairRangeDrawer, setShowFairRangeDrawer] = useState(false);
  const mobile = isMobile();
  const fairRangeTitle = t(
    'price.fields.set_your_rate.fair_range_estimate_link',
    {
      defaultValue: 'How we estimate fair price range'
    }
  );
  const fairRangeContent = t('price.fields.set_your_rate.fair_range_tooltip', {
    defaultValue:
      "The fair price range is the market rate for this deal. It's estimated from recent deals by similar creators, factoring in your audience, niche, channel performance (e.g.average views, follower counts), and how well you fit this brand. Pricing within this range makes sure you're paid what your work is worth while keeping your chance of landing the collaboration as high as possible."
  });
  const fairRangeLink = (
    <div className="flex items-center gap-1">
      <TipsIcon />
      <div className="justify-start text-text-primary text-sm font-normal font-['DM_Sans'] leading-5">
        {fairRangeTitle}
      </div>
    </div>
  );
  useEffect(() => {
    if (!isCounterOffer) return;
    if (
      (lastAdsOfferAmount && +newOrderPrice < lastAdsOfferAmount) ||
      (lastCreatorOfferAmount && +newOrderPrice > +lastCreatorOfferAmount)
    ) {
      setIsPriceRange(false);
    } else {
      setIsPriceRange(true);
    }
  }, [
    lastAdsOfferAmount,
    lastCreatorOfferAmount,
    newOrderPrice,
    isCounterOffer
  ]);

  // 切换价格套餐后，重新对比价格提示
  useEffect(() => {
    // 价格限制不同，和更新价格后，重置base rate 设置成功状态
    setSetBaseRateSuccess(false);
  }, [newOrderPrice]);

  // 切换选中的订单类型后，重置base rate 设置成功状态
  useEffect(() => {
    setSetBaseRateSuccess(false);
  }, [selectedPriceFields]);

  // 议价场景：仅用达人上次出价初始化一次
  useEffect(() => {
    if (hasManuallyAdjustedRef.current || hasSetCounterOfferInitialRef.current)
      return;
    if (!isCounterOffer || isEmptyFairRangeValue(lastCreatorOfferAmount))
      return;

    setNewOrderPrice(Math.round(Number(lastCreatorOfferAmount)));
    hasSetCounterOfferInitialRef.current = true;
  }, [isCounterOffer, lastCreatorOfferAmount, setNewOrderPrice]);

  // 仅在 low/high 变更时重置公允区间默认值（切换内容形式后无论是否手动调价均重置）
  useEffect(() => {
    if (low == null || high == null) return;

    const prev = prevLowHighRef.current;
    const isLowHighChanged =
      prev == null
        ? !isCounterOffer || isEmptyFairRangeValue(lastCreatorOfferAmount)
        : prev.low !== low || prev.high !== high;

    prevLowHighRef.current = { low, high };

    if (!isLowHighChanged) return;

    hasManuallyAdjustedRef.current = false;
    setNewOrderPrice(getFairRangeDefaultValue(low, high));
  }, [low, high, setNewOrderPrice, isCounterOffer, lastCreatorOfferAmount]);

  useEffect(() => {
    if (!visible) return;
    const interestedPriceFields = selectedPriceFields;
    if (interestedPriceFields?.length === 0 || !detailData?.order?.id) {
      return;
    }
    const params = {
      interestedPriceField: {
        ...interestedPriceFields[0]
      }
    };
    axiosInstance
      .post(`/order/${detailData.order.id}/competitivePrice`, params)
      .then(({ data }) => {
        const interestedPriceField = data?.data?.interestedPriceField;
        useMultiplePricesStore.setState({
          creatorLowActualGet:
            interestedPriceField?.fairPriceSuggestion?.creatorLowActualGet ??
            null,
          creatorHighActualGet:
            interestedPriceField?.fairPriceSuggestion?.creatorHighActualGet ??
            null,
          creatorMaxActualGet: interestedPriceField?.creatorMaxActualGet ?? null
        });
      })
      .catch(() => {});
  }, [detailData, selectedPriceFields, visible]);

  const finishEditing = () => {
    const trimmed = String(newOrderPrice ?? '').trim();
    if (trimmed && +trimmed > 0) {
      setNewOrderPrice(Math.round(+trimmed));
    } else if (!trimmed) {
      setNewOrderPrice('');
    }
    setIsEditing(false);
  };

  const handlePriceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setNewOrderPriceFromUser('');
      return;
    }
    if (/^\d+$/.test(value)) {
      setNewOrderPriceFromUser(value);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 justify-between">
        <div className="justify-start text-text-primary text-base font-semibold font-['DM_Sans'] leading-6">
          {isCounterOffer
            ? t('modal.modal_counter_offer.your_counter_rate', {
                defaultValue: 'Your counter offer'
              })
            : disabled && submitedBy === 'ads'
              ? t('price.fields.set_your_rate.brand_fixed_rate', {
                  defaultValue: 'Brand fixed rate'
                })
              : t('price.fields.set_your_rate.share_rate_title', {
                  defaultValue: 'Share your rate'
                })}
        </div>
        {mobile ? (
          <>
            <div
              className="cursor-pointer"
              onClick={() => setShowFairRangeDrawer(true)}
            >
              {fairRangeLink}
            </div>
            <Drawer
              open={showFairRangeDrawer}
              onClose={() => setShowFairRangeDrawer(false)}
              placement="bottom"
              height="auto"
              closable={false}
              drawerStyle={{
                borderRadius: '16px 16px 0 0'
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div className="flex flex-col gap-4 px-4 pt-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))]">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="flex-shrink-0"
                    onClick={() => setShowFairRangeDrawer(false)}
                  >
                    <LeftArrowIcon className="w-6 h-6" />
                  </button>
                  <div className="justify-start text-Colors-Text-text-primary-(900) text-lg font-semibold font-['Inter'] leading-7">
                    {fairRangeTitle}
                  </div>
                </div>
                <div className="self-stretch justify-start text-Colors-Text-text-primary-(900) text-lg font-normal font-['Inter'] leading-7">
                  {fairRangeContent}
                </div>
              </div>
            </Drawer>
          </>
        ) : (
          <Tooltip content={fairRangeContent}>{fairRangeLink}</Tooltip>
        )}
      </div>
      <div className="border border-[#E7E5E4] rounded-md p-4 flex flex-col gap-4">
        <div className="flex items-end justify-center gap-2">
          <div className="flex items-end gap-1">
            <span className="text-[#44403C] text-2xl font-semibold font-['DM_Sans'] leading-8">
              $
            </span>
            {isEditing && !disabled ? (
              <span className="relative inline-block">
                <span
                  aria-hidden
                  className="invisible block whitespace-pre text-[#44403C] text-5xl font-semibold font-['DM_Sans'] leading-none"
                >
                  {displayPrice}
                </span>
                <input
                  type="number"
                  autoFocus
                  step={1}
                  disabled={detailData?.order?.fixedCreatorEarnAmount}
                  value={newOrderPrice ?? ''}
                  className="absolute left-0 top-0 m-0 h-full w-full border-none bg-transparent p-0 text-[#44403C] text-5xl font-semibold font-['DM_Sans'] leading-none outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  onChange={handlePriceInputChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  onBlur={finishEditing}
                />
              </span>
            ) : (
              <span
                className="text-[#44403C] text-5xl font-semibold font-['DM_Sans'] cursor-pointer"
                onClick={() => !disabled && setIsEditing(true)}
              >
                {disabled
                  ? formatPriceWithCommas(order?.lastPrice || newOrderPrice)
                  : formatPriceWithCommas(newOrderPrice)}
              </span>
            )}
          </div>
          {!isEditing && !disabled && (
            <div className="w-6 h-6 flex-shrink-0">
              <div
                className="cursor-pointer"
                onClick={() => !disabled && setIsEditing(true)}
              >
                <EditIcon className="w-6 h-6 text-[#A9A29D]" />
              </div>
            </div>
          )}
          {!order?.isPriceNegotiable && disabled && (
            <div className="flex items-center gap-[2px] pb-1">
              <LockIcon className="w-5 h-5 text-[#A9A29D]" />
              <div className="justify-start text-[#737373] text-lg font-normal font-['Inter'] leading-5">
                {t('price.fields.set_your_rate.non_negotiable_label', {
                  defaultValue: 'Non-negotiable'
                })}
              </div>
            </div>
          )}
        </div>
        {low != null && high != null && (
          <EstimateFairRange
            low={low}
            high={high}
            value={newOrderPrice}
            setValue={setNewOrderPriceFromUser}
            lastAdsOfferAmount={lastAdsOfferAmount}
            lastCreatorOfferAmount={lastCreatorOfferAmount}
            disabled={disabled}
          />
        )}
        {!disabled && !isPriceRange && (
          <Alert
            type="error"
            description={t('modal.modal_counter_offer.allowed_range', {
              defaultValue: 'Allowed range: {{brandRate}}–{{yourRate}} USD',
              brandRate: formatPriceDollar(lastAdsOfferAmount),
              yourRate: formatPriceDollar(lastCreatorOfferAmount)
            })}
          />
        )}
        {!disabled &&
          low != null &&
          high != null &&
          creatorMaxActualGet != null &&
          isPriceRange && (
            <EstimateFairTips
              low={low}
              high={high}
              creatorMaxActualGet={creatorMaxActualGet as number}
              newOrderPrice={newOrderPrice as number | string}
              setBaseRateSuccess={setBaseRateSuccess}
              setSetBaseRateSuccess={setSetBaseRateSuccess}
            />
          )}
        {!order?.isPriceNegotiable && disabled && (
          <Alert
            type="info"
            description={t('price.fields.set_your_rate.non_negotiable_alert', {
              defaultValue:
                'This rate is non-negotiable. You can accept or decline.'
            })}
          />
        )}
      </div>
    </div>
  );
}
